<script setup lang="ts">
/**
 * PAGE CAGNOTTE — variante C : la bannière de la cagnotte, en grand dans la colonne
 * de gauche, flèches et points DANS l'image (esprit de la page actuelle).
 * Fondu entre les photos ; un clic ouvre la visionneuse plein écran.
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import PageCagnotteGalerie from "./PageCagnotteGalerie.vue";

const props = defineProps<{ images: string[] }>();
const { getImageUrl } = useRechercheHelpers();

const index = ref(0);
const aller = (pas: number) => {
  index.value = (index.value + pas + props.images.length) % props.images.length;
};
const galerie = ref<number | null>(null);

// Glisser au doigt (mobile).
let departX: number | null = null;
function debutToucher(e: TouchEvent) {
  departX = e.touches[0]?.clientX ?? null;
}
function finToucher(e: TouchEvent) {
  const finX = e.changedTouches[0]?.clientX;
  if (departX === null || finX === undefined) return;
  const ecart = finX - departX;
  if (Math.abs(ecart) > 40) aller(ecart < 0 ? 1 : -1);
  departX = null;
}
</script>

<template>
  <div
    v-if="images.length"
    class="group relative aspect-[16/10] rounded-[28px] overflow-hidden bg-gray-100"
    @touchstart.passive="debutToucher"
    @touchend.passive="finToucher"
  >
    <TransitionGroup
      enter-active-class="transition-opacity duration-700 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-700 ease-out absolute inset-0"
      leave-to-class="opacity-0"
    >
      <button
        :key="images[index]"
        type="button"
        class="absolute inset-0 w-full h-full cursor-zoom-in"
        :aria-label="$t('campagne.pageCagnotte.agrandirPhoto', { n: index + 1 })"
        @click="galerie = index"
      >
        <img
          :src="getImageUrl(images[index] ?? null, undefined, 1400)"
          alt=""
          class="w-full h-full object-cover"
          :loading="index === 0 ? 'eager' : 'lazy'"
        />
      </button>
    </TransitionGroup>

    <template v-if="images.length > 1">
      <!-- Voile bas pour la lisibilité des points -->
      <div class="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />

      <button
        type="button"
        class="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur text-(--color-ink) shadow-md flex items-center justify-center transition-[opacity,scale] duration-200 sm:opacity-0 sm:group-hover:opacity-100 hover:scale-105"
        :aria-label="$t('campagne.pageCagnotte.photoPrecedente')"
        @click="aller(-1)"
      >
        <UIcon name="i-lucide-chevron-left" class="size-5" />
      </button>
      <button
        type="button"
        class="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur text-(--color-ink) shadow-md flex items-center justify-center transition-[opacity,scale] duration-200 sm:opacity-0 sm:group-hover:opacity-100 hover:scale-105"
        :aria-label="$t('campagne.pageCagnotte.photoSuivante')"
        @click="aller(1)"
      >
        <UIcon name="i-lucide-chevron-right" class="size-5" />
      </button>

      <div class="absolute bottom-4 inset-x-0 flex justify-center gap-1.5">
        <button
          v-for="(img, i) in images"
          :key="img"
          type="button"
          class="h-2 rounded-full transition-all duration-300"
          :class="i === index ? 'w-6 bg-white' : 'w-2 bg-white/60 hover:bg-white/90'"
          :aria-label="$t('campagne.pageCagnotte.photoN', { n: i + 1 })"
          @click="index = i"
        />
      </div>

      <span class="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/45 backdrop-blur text-white text-xs" style="font-weight: 600 !important">
        {{ index + 1 }} / {{ images.length }}
      </span>
    </template>

    <PageCagnotteGalerie v-if="galerie !== null" :images="images" :depart="galerie" @fermer="galerie = null" />
  </div>
</template>
